import React, { useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, AlertTriangle, X, Loader2, MapPin, Camera } from "lucide-react";

/**
 * ForensicCheckButton. small button on photo documents. Clicks open a modal
 * that fetches /documents/:id/forensic and shows whether the stated chronology
 * date matches the EXIF "captured_at" date. If they mismatch by more than a
 * day, flagged in red. useful for catching landlord-supplied photos with
 * mis-stated dates.
 */
export default function ForensicCheck({ doc }) {
 const [open, setOpen] = useState(false);
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);

 const fetchIt = async () => {
 if (data || loading) return;
 setLoading(true);
 try {
 const res = await api.get(`/documents/${doc.id}/forensic`);
 setData(res.data);
 } finally { setLoading(false); }
 };

 const onClick = () => {
 setOpen(true);
 fetchIt();
 };

 return (
 <>
 <button onClick={onClick}
 className="btn-ghost text-[11px] inline-flex items-center gap-1"
 title="Compare the stated date with the date the photo was actually taken"
 data-testid={`forensic-btn-${doc.id}`}>
 <ShieldCheck size={12} strokeWidth={1.5} /> Check photo's real date
 </button>

 {open && (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
 <div onClick={e => e.stopPropagation()} className="bg-white max-w-md w-full shadow-2xl" data-testid="forensic-modal">
 <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E4DE]">
 <div className="flex items-center gap-2">
 <ShieldCheck size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h3 className="font-serif text-lg text-[#0D2B52]">When was this photo really taken?</h3>
 </div>
 <button onClick={() => setOpen(false)} className="btn-ghost p-1"><X size={16} strokeWidth={1.5} /></button>
 </div>

 <div className="p-5">
 {loading && (
 <div className="text-center py-6">
 <Loader2 size={20} strokeWidth={1.5} className="mx-auto text-[#1B6C62] animate-spin mb-2" />
 <p className="text-sm text-[#6B7280] italic">Reading the photo's EXIF…</p>
 </div>
 )}

 {data && !data.exif_present && (
 <div className="text-sm text-[#6B7280]">
 No EXIF metadata could be read from this file. Some screenshots, scans and re-encoded photos have their EXIF stripped. That&apos;s normal.
 </div>
 )}

 {data && data.exif_present && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <div className="label-mono mb-1">Stated date</div>
 <div className="font-mono text-[#0D2B52]">{data.comparison.stated_date || ", not set,"}</div>
 </div>
 <div>
 <div className="label-mono mb-1">Photo's actual date (EXIF)</div>
 <div className="font-mono text-[#0D2B52]">{(data.exif.captured_at || "").slice(0, 19) || ","}</div>
 </div>
 </div>

 {data.comparison.flag === "match" && (
 <FlagBox tone="ok" title="Dates match">
 {data.comparison.explanation}
 </FlagBox>
 )}
 {data.comparison.flag === "within_a_day" && (
 <FlagBox tone="ok" title="Within 1 day">
 {data.comparison.explanation}
 </FlagBox>
 )}
 {data.comparison.flag === "mismatch" && (
 <FlagBox tone="warn" title={`Mismatch. ${data.comparison.delta_days} days apart`}>
 {data.comparison.explanation}
 </FlagBox>
 )}
 {data.comparison.flag === "no_data" && (
 <FlagBox tone="muted" title="Can't compare yet">
 {data.comparison.explanation}
 </FlagBox>
 )}

 {(data.exif.Make || data.exif.Model) && (
 <div className="text-xs text-[#6B7280] flex items-center gap-1.5">
 <Camera size={12} strokeWidth={1.5} />
 Taken on {data.exif.Make} {data.exif.Model}
 </div>
 )}
 {data.exif.gps_lat != null && data.exif.gps_lon != null && (
 <div className="text-xs text-[#6B7280] flex items-center gap-1.5">
 <MapPin size={12} strokeWidth={1.5} />
 GPS: {data.exif.gps_lat.toFixed(4)}, {data.exif.gps_lon.toFixed(4)}
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </>
 );
}

function FlagBox({ tone, title, children }) {
 const styles = {
 ok: "bg-[#E6F5F2] border-[#1B6C62]/30 text-[#0D2B52]",
 warn: "bg-[#FAF0EC] border-[#B35D4C]/30 text-[#0D2B52]",
 muted: "bg-[#FAF9F6] border-[#E5E4DE] text-[#6B7280]",
 };
 const Icon = tone === "warn" ? AlertTriangle : ShieldCheck;
 const iconColor = tone === "warn" ? "text-[#B35D4C]" : (tone === "ok" ? "text-[#1B6C62]" : "text-[#6B7280]");
 return (
 <div className={`p-3 border ${styles[tone]}`}>
 <div className="flex items-center gap-2 mb-1">
 <Icon size={14} strokeWidth={1.75} className={iconColor} />
 <span className="font-serif text-base">{title}</span>
 </div>
 <p className="text-[13px] leading-relaxed">{children}</p>
 </div>
 );
}
